/* A liquidation price worked out rather than read.
 *
 * Entry, margin and leverage are enough to say roughly where a position gets
 * liquidated, and refusing to open one by hand until the exchange's figure was
 * typed in stopped the whole entry over a number the other three nearly give.
 * Nearly: MEXC liquidates once the margin left falls to a maintenance
 * requirement that differs by contract and by size, and automatic margin
 * addition moves the price again. So the figure here is an estimate, marked as
 * one everywhere it is shown, and replaced by the exchange's the moment one is
 * typed in or read with the key.
 *
 * It errs towards the entry. The maintenance share assumed is one percent — the
 * MU contract this book holds works out at about 0.4 — so the estimate usually
 * sits a little nearer than the real price, which is the direction to be wrong
 * in about the price that closes the position. At high leverage the share is
 * capped at half the initial margin, or a hundred-times position would come
 * out liquidated at its own entry. */

export const ASSUMED_MAINTENANCE = 0.01;

export function estimateLiquidation(side: 'long' | 'short', entry: number, leverage: number): number | null {
  if (!(entry > 0) || !(leverage > 0) || !Number.isFinite(entry) || !Number.isFinite(leverage)) return null;
  const initial = 1 / leverage;
  const maintenance = Math.min(ASSUMED_MAINTENANCE, initial / 2);
  const move = initial - maintenance;
  return side === 'long' ? entry * (1 - move) : entry * (1 + move);
}

/** What a form saves: the figure typed, or an estimate when the field is left
    empty. Null when there is neither — no entry or leverage to work from. */
export function liquidationFor(
  typed: number, side: 'long' | 'short', entry: number, leverage: number,
): { price: number; estimated: boolean } | null {
  if (Number.isFinite(typed) && typed > 0) return { price: typed, estimated: false };
  const estimate = estimateLiquidation(side, entry, leverage);
  return estimate === null ? null : { price: estimate, estimated: true };
}
